import { useMemo, useState } from 'react';
import { Dialog } from '../../components/Dialog.tsx';
import { COMMANDS, formatShortcut, type Command } from '../../lib/commands.ts';
import { tip } from '../../lib/tooltip.ts';

function shortcuts(c: Command): string[] {
  return Array.isArray(c.shortcut) ? c.shortcut : c.shortcut ? [c.shortcut] : [];
}

export function CommandPalette({ onClose }: { onClose(): void }) {
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  const results = useMemo(() => {
    const q = query.toLowerCase().trim();
    return COMMANDS.filter((c) => c.id !== 'palette' && (!q || c.label.toLowerCase().includes(q)));
  }, [query]);
  const run = (c: Command | undefined) => {
    if (!c) return;
    onClose();
    // Defer so the dialog unmounts before the command (which may open another dialog) runs.
    setTimeout(() => c.run(), 0);
  };
  return (
    <Dialog title="Commands" onClose={onClose}>
      <input
        className="input palette-input"
        autoFocus
        placeholder="Type a command…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setIndex(0);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') setIndex((i) => Math.min(results.length - 1, i + 1));
          else if (e.key === 'ArrowUp') setIndex((i) => Math.max(0, i - 1));
          else if (e.key === 'Enter') run(results[index]);
          else return;
          e.preventDefault();
        }}
      />
      <div className="palette-list">
        {results.map((c, i) => (
          <button
            key={c.id}
            className={`palette-item${i === index ? ' active' : ''}`}
            {...tip(`Run “${c.label}”`, shortcuts(c)[0])}
            onMouseEnter={() => setIndex(i)}
            onClick={() => run(c)}
          >
            <span>{c.label}</span>
            {shortcuts(c)[0] && <span className="kbd">{formatShortcut(shortcuts(c)[0]!)}</span>}
          </button>
        ))}
      </div>
    </Dialog>
  );
}

export function ShortcutsDialog({ onClose }: { onClose(): void }) {
  return (
    <Dialog title="Keyboard shortcuts" onClose={onClose} width={560}>
      <div className="shortcut-list">
        {COMMANDS.filter((c) => shortcuts(c).length > 0).map((c) => (
          <div key={c.id} className="shortcut-row">
            <span>{c.label}</span>
            <span>
              {shortcuts(c).map((s) => (
                <span key={s} className="kbd">
                  {formatShortcut(s)}
                </span>
              ))}
            </span>
          </div>
        ))}
        <div className="shortcut-row">
          <span>Zoom timeline</span>
          <span className="kbd">{formatShortcut('Mod')} + scroll</span>
        </div>
        <div className="shortcut-row">
          <span>Ripple trim while dragging an edge</span>
          <span className="kbd">{formatShortcut('Alt')} + drag</span>
        </div>
        <div className="shortcut-row">
          <span>Disable snapping while dragging</span>
          <span className="kbd">{formatShortcut('Shift')} + drag</span>
        </div>
        <div className="shortcut-row">
          <span>Insert (instead of overwrite) when dropping media</span>
          <span className="kbd">{formatShortcut('Alt')} + drop</span>
        </div>
      </div>
    </Dialog>
  );
}
